<template>
  <div class="notes-bar">
    <div class="notes-content">
      <ul
        class="tabs tabs-container"
        data-tabs
        id="assembly-bar-tabs"
        data-deep-link="true"
      >
        <li class="tabs-title is-active">
          <a href="#summary" aria-selected="true">{{ $t('summary') }}</a>
        </li>
        <li class="tabs-title">
          <a href="#parts">{{ $t('parts') }}</a>
        </li>
        <li class="tabs-title">
          <a href="#guide">{{ $t('guide') }}</a>
        </li>
      </ul>
      <div v-if="this.loading" class="text-center loader">
        <BeatLoader v-if="this.loading" />
      </div>
      <div
        v-else-if="this.error"
        class="text-center margin-top-1"
      >
        {{ $t('loadingError') }}
      </div>

      <div v-else class="tabs-content" data-tabs-content="assembly-bar-tabs">
        <div class="tabs-panel is-active" id="summary">
          <div class="assembly-summary">
            <div v-if="this.assemblySummary.sides.length > 0" class="grid-x summary-line">
              <div class="cell small-6">
                {{ $t('sides') }}
              </div>
              <div v-if="this.assemblySummary.sides.length === 1" class="cell small-6 text-right">
                {{ $t('singleSided', { side: this.assemblySummary.sides[0] }) }}
              </div>
              <div v-else class="cell small-6 text-right">
                {{ $t('doubleSided') }}
              </div>
            </div>
            <div class="grid-x summary-line">
              <div class="cell small-6">
                {{ $t('distinctComponents') }}
              </div>
              <div class="cell small-6 text-right">
                {{ this.assemblySummary.totalComponents }}
              </div>
            </div>
            <div class="grid-x summary-line">
              <div class="cell small-6">
                {{ $t('smtComponents') }}
              </div>
              <div class="cell small-6 text-right">
                {{ this.assemblySummary.smtCount }}
              </div>
            </div>
            <div class="grid-x summary-line">
              <div class="cell small-6">
                {{ $t('thtComponents') }}
              </div>
              <div class="cell small-6 text-right">
                {{ this.assemblySummary.thtCount }}
              </div>
            </div>
            <div class="grid-x summary-line">
              <div class="cell small-6">
                {{ $t('pressFitComponents') }}
              </div>
              <div class="cell small-6 text-right">
                {{ this.assemblySummary.pressFitCount }}
              </div>
            </div>
          </div>
          <hr class="subtle--full">
          <div v-if="this.notes.length === 0" class="cell assembly-ready text-center">
            <i class="fas fa-3x fa-check-circle success-overview text-center" />
            <div>{{ $t('pcbReady') }}</div>
          </div>
          <ul
            id="note-accordion"
            class="vertical menu accordion-menu notes-menu"
            data-accordion-menu
          >
            <li v-for="(group, noteIdx) in this.groupedNotes" :key="noteIdx" class="note-wrapper">
              <a class="note-title" href="#">
                <div>
                  <i
                    v-if="group.priority === 'critical'"
                    class="fas fa-minus-circle critical-overview text-center"
                  />
                  <i
                    v-if="group.priority === 'warning'"
                    class="fas fa-exclamation-triangle warning-overview text-center"
                  />
                  <i
                    v-if="group.priority === 'relevant'"
                    class="fas fa-info-circle relevant-overview text-center"
                  />
                  <i
                    v-if="group.priority === 'internal'"
                    class="fas fa-glasses internal-overview text-center"
                  />
                  {{ $t(group.category) }}
                </div>
              </a>
              <ul class="menu vertical nested note-cases">
                <li>
                  <div>
                    <p
                      class="notes-description"
                      v-if="$t((group.category + '_description'), 'en')"
                      v-html="$t(group.category + '_description')"
                    />
                  </div>
                </li>
                <li v-for="(note, index) in group.notes" :key="index">
                  <a
                    @click="drawNote(note)"
                    class="note-case"
                    :class="{ 'drawn': note.selected }"
                    href="#"
                  >
                    <div class="case-text">
                      <div v-if="note.identifiers.length === 0" class="case-number">
                        Case #{{ index + 1 }}
                      </div>
                      <div v-else class="case-number">{{ note.identifiers.join(", ") }}</div>
                      <div v-if="note.shapes.length !== 0">
                        <i v-if="note.selected" class="indicator fas fa-eye" />
                        <i v-else class="indicator fas fa-eye-slash" />
                      </div>
                    </div>
                  </a>
                  <hr class="subtle--full">
                </li>
              </ul>
            </li>
          </ul>
          <div v-if="this.notes.length > 0" class="analyze-notice">
            {{ $t('analyzeNotice') }}
          </div>
        </div>

        <div class="tabs-panel" id="parts">
          <div class="grid-x grid-padding-x mini-space">
            <div class="cell small-12">
              <div class="input-group">
                <span class="input-group-label"><i class="fa fa-search" /></span>
                <input type="search" v-model="externalQuery" class="input-group-field">
              </div>
            </div>

            <div class="cell small-12">
              <VueGoodTable
                class="part-table"
                :columns="partTableColumns"
                :rows="parts"
                :search-options="{
                  enabled: true,
                  externalQuery: externalQuery,
                  searchFn: searchFn,
                }"
                :select-options="{
                  enabled: true,
                  selectOnCheckboxOnly: true,
                  disableSelectInfo: true,
                }"
                @selected-rows-change="partSelectionChanged"
                style-class="vgt-table bordered"
              >
                <template #emptystate>
                  {{ $t('noPartsNotice') }}
                </template>
                <template #table-row="props">
                  <template v-if="props.column.field === 'place'">
                    <i class="far fa-check-square" v-if="props.row.place" />
                    <i class="far fa-square" v-else />
                    <span v-if="props.row.mountType">&nbsp;{{ $t(props.row.mountType) }}</span>
                  </template>
                  <span v-else-if="props.column.field === 'value'">
                    {{ props.row.matchDescription || props.row.mpn || props.row.value }}
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>
              </VueGoodTable>
            </div>
          </div>
        </div>

        <div class="tabs-panel" id="guide">
          <Guide :parts="guideParts" :project-token="projectToken" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { components } from 'locales';
import { getRequest } from 'helper/request';
import $ from 'jquery';
import Foundation from 'foundation-sites';

import { VueGoodTable } from 'vue-good-table-next';
import Guide from 'components/boards/Guide.vue';
import BeatLoader from 'components/shared/BeatLoader.vue';

export default {
  name: 'AssemblyBar',
  props: {
    metaPath: String,
    projectToken: String,
  },
  components: {
    BeatLoader,
    Guide,
    VueGoodTable,
  },
  data() {
    return {
      loading: false,
      notes: [],
      assemblySummary: {
        sides: [],
        totalComponents: null,
        smtCount: null,
        thtCount: null,
      },
      groupedNotes: [],
      parts: [],
      guideParts: [],
      bus: this.$root.bus,
      externalQuery: '',
      partTableColumns: [
        {
          field: 'name',
          label: this.$t('name'),
        },
        {
          field: 'value',
          label: this.$t('value'),
        },
        {
          field: 'place',
          label: this.$t('place'),
          html: true,
          globalSearchDisabled: true,
        },
        {
          field: 'side',
          label: this.$t('side'),
          formatFn: (val) => this.$t(val),
          globalSearchDisabled: true,
        },
      ],
      error: null,
    };
  },
  methods: {
    loadMeta() {
      getRequest(this.metaPath).then((data) => {
        this.notes = data.notes.map((note) => {
          note.selected = false;
          return note;
        });
        this.assemblySummary = data.assemblySummary;
        this.parts = data.parts;
        this.guideParts = data.guide;
        this.bus.emit('loadParts', this.parts);
        this.bus.emit('updatePartPlacement', this.parts.filter((part) => part.place));
        this.groupNotes();
      }).catch((e) => {
        this.loading = false;
        this.error = e;
      }).finally(() => {
        this.loading = false;
      });
    },
    groupNotes() {
      const groupedNotes = [];

      this.notes.forEach((note) => {
        const noteGroup = groupedNotes.find((group) => group.category === note.category);
        if (noteGroup) {
          noteGroup.notes.push(note);
        } else {
          groupedNotes.push({
            category: note.category,
            priority: note.priority,
            notes: [note],
          });
        }
      });
      this.groupedNotes = groupedNotes.sort((group1, group2) => {
        const priorities = {
          critical: 1,
          warning: 2,
          relevant: 3,
          internal: 4,
        };
        return priorities[group1.priority] - priorities[group2.priority];
      });
    },
    drawNote(note) {
      if (note.shapes.length > 0) {
        note.selected = !note.selected;
        const method = note.selected ? 'addNote' : 'removeNote';
        this.bus.emit(method, note);
      } else if (note.identifiers.length > 0) {
        const parts = this.parts.filter((part) => note.identifiers.includes(part.name));
        this.bus.emit('updatePartFocus', { parts, updateCamera: true });
      }
    },
    initBus() {
      const self = this;
      self.bus.on('partClicked', (id) => {
        self.parts.forEach((part) => {
          if (part.id === id) {
            self.externalQuery = part.name;
            self.bus.emit('updatePartFocus', { parts: [part], updateCamera: false });
          }
        });
      });
    },
    tabGuideSelected() {
      this.bus.emit('updatePartFocus', { parts: [] });
      this.bus.emit('updatePartPlacement', []);
    },
    tabPartsSelected() {
      this.bus.emit('updatePartFocus', { parts: [] });
      this.bus.emit('updatePartPlacement', this.parts.filter((part) => part.place));
    },
    partSelectionChanged(evt) {
      this.bus.emit('updatePartFocus', { parts: evt.selectedRows });
    },
    searchFn(row, col, cellValue, searchTerm) {
      if (searchTerm.length === 0) { return true; }
      if (cellValue === null) { return false; }

      const fullTextSearchColumns = ['value'];
      if (fullTextSearchColumns.includes(col.field)) {
        return cellValue.toLowerCase().includes(searchTerm.trim().toLowerCase());
      }

      return searchTerm.trim().toLowerCase() === cellValue.toLowerCase();
    },
  },
  mounted() {
    this.loading = true;
    const self = this;
    this.bus.on('viewerReady', () => {
      self.bus.emit('loadParts', self.parts);
      self.bus.emit('updatePartPlacement', self.parts.filter((part) => part.place));
    });
    this.loadMeta();
    this.initBus();
  },
  updated() {
    const self = this;
    this.$nextTick(() => {
      // Foundation requires JQuery reference -.-
      self.tabs = new Foundation.Tabs($('#assembly-bar-tabs'));
      self.accordion = new Foundation.AccordionMenu($('#note-accordion'));
    });
  },
  unmounted() {
    this.tabs.destroy();
    this.accordion.destroy();
  },

  i18n: components.boards.AssemblyBar,
};
</script>

<style scoped lang="scss">
@import 'stylesheets/foundation/colors';

.notes-header {
  display: flex;
  flex-direction: row;
  width: 100%;

  h5 {
    margin: 0;
    padding-right: 3rem;
  }
}

.notes-overview {
  padding-top: 0.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 12%;
  font-size: 1.2rem;

  i {
    font-size: 1.2rem;
  }

  div {
    display: flex;
    flex-direction: column;
  }
}

.critical-overview {
  color: map-get($foundation-palette, 'danger');
}

.warning-overview {
  color: map-get($foundation-palette, 'warning');
}

.relevant-overview {
  color: $blue;
}

.internal-overview {
  color: $dark-gray;
}

.success-overview {
  color: $green
}

.part-table {
  .vgt-table {
    margin-bottom: 0;
    border-radius: 0;
  }
}

.no-notes {
  padding: 0 3rem 0 3rem;
  display: flex;
  flex-direction: column;

  i {
    padding: 2rem;
    font-size: 5rem;
  }
}

.assembly-summary {
  padding: 0 2rem 0 2rem;
}

.assembly-ready {
  padding: 0 3rem 0 3rem;

  i {
    padding: 2rem;
    font-size: 5rem;
  }
}

.summary-line {
  padding-top: 0.6rem;
}

.no-data-notice {
  a {
    display: contents;
  }
}

.note-title {
  color: black;
  min-height: 54px;
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;

  i {
    margin-right: 0.5rem;
  }
}

.note-wrapper:last-child {
  border-bottom-style: solid;
  border-color: #acacac;
  border-bottom-width: 1px;
}

.note-case:hover {
  color: black;
  background-color: #cacaca;
}

.drawn {
  color: black !important;
  background-color: #cacaca !important;
}

.note-case {
  border: 1px solid transparent;
  color: grey;
  background-color: white;
  flex-direction: column;
  justify-content: center;

  .case-text {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
}

.note-case:focus {
  color: grey;
}

.notes-description {
  padding: 0.7rem 1rem;
  font-size: small;
  margin-bottom: 0;

  a {
    display: unset !important;
    padding: 0 !important;
  }
}

.note-cases {
  color: black;
  margin-left: 0 !important;

  hr {
    width: 100%;
    margin: 0 !important;
  }

  .indicator {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .case-number {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}

.analyze-notice {
  padding: 1rem;
  font-size: 0.9rem;
}

.tabs-container {
  border: 1px solid transparent;

  li.is-active {
    border-bottom: 2px solid $orange;
  }
}

.tabs-panel {
  padding: 0;
}
</style>
